import _ from "lodash";
import React, { useEffect, useRef } from "react";

import classNames from "classnames";
import { colors } from "Constants";
import classes from "./SharedAvatarListPopover.scss";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { Avatar } from "@toddle-edu/ds-web";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { useIntersectionObserver } from "Utils";

const SharedAvatarListSkeletonLoader = props => {
  const { dummyCount = 6 } = props;

  return (
    <div className={classes.skeletonBlockContainer}>
      {_.times(dummyCount, index => (
        <div key={index} className={classes.skeletonBlock}>
          <SkeletonLoader width="24px" height="20px" />
          <SkeletonLoader width="220px" height="12px" />
        </div>
      ))}
    </div>
  );
};

const SharedAvatarList = ({
  isLoading,
  isData,
  showPaginationLoader,
  loaderRef,
  collaborators,
}) => {
  const containerRef = useRef(null);

  const showLoader = isLoading && !isData;
  const handleItemClick = event => {
    event.stopPropagation();
  };

  return (
    <div
      className={classes.container}
      ref={containerRef}
      onClick={handleItemClick}
    >
      {showLoader ? (
        <SharedAvatarListSkeletonLoader />
      ) : (
        <>
          {_.map(collaborators, edge => {
            const {
              id,
              profileImage,
              name,
              acronym,
              color = "",
              iconType = "",
              AvatarIcon = null,
              isUserType,
            } = edge;
            return (
              <div
                className={classes.itemContainer}
                key={id}
                onClick={handleItemClick}
              >
                <Avatar
                  id={id}
                  name={acronym || name}
                  dsVersion="2.0"
                  theme="light"
                  shape="square"
                  size="xx-small"
                  icon={<AvatarIcon size="xx-small" />}
                  iconType={iconType}
                  src={profileImage}
                  color={color}
                />
                <div className={classNames("text-body")}>{name}</div>
              </div>
            );
          })}
        </>
      )}
      {showPaginationLoader && (
        <div ref={loaderRef}>
          <SharedAvatarListSkeletonLoader />
        </div>
      )}
    </div>
  );
};

const SharedAvatarListPopover = ({
  setActiveUnitPlanId,
  nodeRef,
  isLoading,
  isData,
  moreDataLoading,
  hasNextPage = false,
  loadMoreFeed = null,
  networkStatus,
  showPaginationLoader = false,
  isFetchingMoreData,
  popOverData,
}) => {
  const onClose = () => {
    setActiveUnitPlanId(null);
  };

  const loaderRef = useRef(null);
  const isLoaderVisible = useIntersectionObserver(loaderRef, {
    threshold: 0.7,
  });

  useEffect(() => {
    if (isLoaderVisible && showPaginationLoader && !isFetchingMoreData) {
      loadMoreFeed();
    }
  }, [isLoaderVisible, showPaginationLoader, isFetchingMoreData, loadMoreFeed]);

  return (
    <Popover
      isOpen={true}
      positions={["right", "bottom"]}
      reposition={true}
      onClickOutside={onClose}
      shouldCloseOnEsc={true}
      childrenRef={{ current: nodeRef }}
      isChildrenRefPassed={true}
      containerClassName={classes.popoverContainer}
      content={props => {
        const { position, childRect, popoverRect, nudgedTop } = props;
        if (nudgedTop > 120 || nudgedTop < -120) {
          onClose();
        }
        return (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor={colors.white}
            arrowSize={12}
          >
            <SharedAvatarList
              collaborators={popOverData}
              isLoading={isLoading}
              isData={isData}
              moreDataLoading={moreDataLoading}
              hasNextPage={hasNextPage}
              loadMoreFeed={loadMoreFeed}
              networkStatus={networkStatus}
              showPaginationLoader={showPaginationLoader}
              loaderRef={loaderRef}
            />
          </ArrowContainer>
        );
      }}
    />
  );
};

export default SharedAvatarListPopover;
